/**
 * The HTTP client this app uses. No axios.
 *
 * It is a thin layer over fetch, which every browser and every Node version
 * this project runs on already provides. Axios was ~13 kB of the bundle doing
 * what the platform does, and its shape had quietly become the app's error
 * contract: dozens of call sites read `error.response.status`, and two separate
 * axios instances had grown two separate token-refresh implementations that
 * drifted apart.
 *
 * So the surface here is deliberately axios-shaped - `{ data, status }` on the
 * way out, `error.response` on the way in, interceptors, `create()`. Not out of
 * fondness for it, but because changing the transport and every call site in
 * one move would be the kind of change nobody can review. The shape can be
 * simplified later; the dependency is gone now.
 *
 * What it deliberately does NOT do: XSRF cookies, transform stacks, adapters,
 * automatic retries. Nothing in this codebase used them.
 */

export type HttpMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "PATCH"
  | "DELETE"
  | "HEAD"
  | "OPTIONS";

export interface HttpRequestConfig {
  url?: string;
  method?: HttpMethod | Lowercase<HttpMethod>;
  baseURL?: string;
  headers?: Record<string, string>;
  params?: Record<string, unknown> | undefined;
  data?: unknown;
  /** Milliseconds before the request is aborted. 0 disables the timeout. */
  timeout?: number;
  responseType?: "json" | "text" | "blob" | "arraybuffer";
  signal?: AbortSignal;
  /** Callers attach their own bookkeeping here (retry flags, and so on). */
  [key: string]: unknown;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- the response body is
   whatever the endpoint returns. Defaulting the generic to `unknown` instead
   would be more honest and would also mean annotating several hundred existing
   call sites in the same change as swapping the transport underneath them.
   Call sites that pass a type argument still get it checked. */

export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: HttpRequestConfig;
}

/**
 * Carries `response` when the server answered and left it undefined when it did
 * not. Call sites lean on exactly that distinction to tell "the server said no"
 * apart from "we never reached the server".
 */
export class HttpError<T = any> extends Error {
  readonly config: HttpRequestConfig;
  readonly response: HttpResponse<T> | undefined;
  readonly code: string | undefined;

  constructor(
    message: string,
    config: HttpRequestConfig,
    response?: HttpResponse<T>,
    code?: string,
  ) {
    super(message);
    this.name = "HttpError";
    this.config = config;
    this.response = response;
    this.code = code;
  }
}

type Fulfilled<V> = (value: V) => V | Promise<V>;
type Rejected = (error: unknown) => unknown;

interface InterceptorEntry<V> {
  fulfilled?: Fulfilled<V> | undefined;
  rejected?: Rejected | undefined;
}

class InterceptorChain<V> {
  readonly handlers: InterceptorEntry<V>[] = [];

  use(fulfilled?: Fulfilled<V>, rejected?: Rejected): number {
    this.handlers.push({ fulfilled, rejected });
    return this.handlers.length - 1;
  }
}

export interface HttpClient {
  <T = any>(config: HttpRequestConfig): Promise<HttpResponse<T>>;
  request<T = any>(config: HttpRequestConfig): Promise<HttpResponse<T>>;
  get<T = any>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  delete<T = any>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  head<T = any>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  post<T = any>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  put<T = any>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  patch<T = any>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>>;
  defaults: HttpRequestConfig;
  interceptors: {
    request: InterceptorChain<HttpRequestConfig>;
    response: InterceptorChain<HttpResponse<any>>;
  };
}

/** Joins a base and a path without producing "//" or losing a base path. */
function resolveUrl(url: string, baseURL?: string): string {
  if (!baseURL) return url;
  if (/^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) return url;
  return `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
}

function appendParams(url: string, params?: Record<string, unknown>): string {
  if (!params) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    // Undefined and null mean "not set". Serialising them would send the
    // strings "undefined" and "null", which backends read as real values.
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((entry) => search.append(key, String(entry)));
    } else {
      search.append(key, String(value));
    }
  }

  const query = search.toString();
  if (!query) return url;
  return url + (url.includes("?") ? "&" : "?") + query;
}

function isPlainBody(data: unknown): boolean {
  return typeof FormData !== "undefined" && data instanceof FormData
    ? true
    : typeof Blob !== "undefined" && data instanceof Blob
      ? true
      : data instanceof URLSearchParams ||
        data instanceof ArrayBuffer ||
        typeof data === "string";
}

function headerName(
  headers: Record<string, string>,
  name: string,
): string | undefined {
  return Object.keys(headers).find(
    (key) => key.toLowerCase() === name.toLowerCase(),
  );
}

function buildBody(
  data: unknown,
  headers: Record<string, string>,
): BodyInit | undefined {
  if (data === undefined || data === null) return undefined;

  if (isPlainBody(data)) {
    // The browser sets Content-Type for FormData, including the multipart
    // boundary, which we could not compute correctly by hand.
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const existing = headerName(headers, "content-type");
      if (existing) delete headers[existing];
    }
    return data as BodyInit;
  }

  if (!headerName(headers, "content-type")) {
    headers["Content-Type"] = "application/json";
  }
  return JSON.stringify(data);
}

async function parseBody(
  response: Response,
  responseType: HttpRequestConfig["responseType"],
): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;

  switch (responseType) {
    case "blob":
      return response.blob();
    case "arraybuffer":
      return response.arrayBuffer();
    case "text":
      return response.text();
    default:
      break;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      // A body that claims JSON and is not: hand back what actually arrived
      // rather than throwing something unrelated to the real failure.
      return text;
    }
  }

  return response.text();
}

function collectHeaders(response: Response): Record<string, string> {
  const out: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

export function createHttpClient(defaults: HttpRequestConfig = {}): HttpClient {
  const interceptors = {
    request: new InterceptorChain<HttpRequestConfig>(),
    response: new InterceptorChain<HttpResponse<any>>(),
  };

  async function send<T>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
    const headers: Record<string, string> = {
      ...(defaults.headers ?? {}),
      ...(config.headers ?? {}),
    };

    const method = (config.method ?? "GET").toUpperCase() as HttpMethod;
    const url = appendParams(
      resolveUrl(config.url ?? "", config.baseURL ?? defaults.baseURL),
      config.params,
    );
    const body =
      method === "GET" || method === "HEAD"
        ? undefined
        : buildBody(config.data, headers);

    // A request with no body must not announce one. The clients set a default
    // Content-Type of application/json for every request, and Fastify rejects a
    // POST that declares JSON and then sends nothing with
    // FST_ERR_CTP_EMPTY_JSON_BODY - which is what broke passkey enrolment, a
    // POST that needs no arguments at all.
    if (body === undefined) {
      const declared = headerName(headers, "content-type");
      if (declared) delete headers[declared];
    }

    const timeout = config.timeout ?? defaults.timeout ?? 0;
    const controller = new AbortController();
    let timedOut = false;
    const timer =
      timeout > 0
        ? setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, timeout)
        : null;

    // A caller's own signal still has to work alongside the timeout.
    if (config.signal) {
      if (config.signal.aborted) controller.abort();
      else
        config.signal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        ...(body === undefined ? {} : { body }),
        signal: controller.signal,
      });
    } catch (error) {
      if (timedOut) {
        throw new HttpError(
          `timeout of ${timeout}ms exceeded`,
          config,
          undefined,
          "ETIMEDOUT",
        );
      }
      if ((error as Error)?.name === "AbortError") {
        throw new HttpError(
          "Request aborted",
          config,
          undefined,
          "ERR_CANCELED",
        );
      }
      // No response at all - offline, DNS, CORS preflight refused. Call sites
      // check for a missing `response` to tell this from a server error.
      throw new HttpError(
        (error as Error)?.message || "Network Error",
        config,
        undefined,
        "ERR_NETWORK",
      );
    } finally {
      if (timer) clearTimeout(timer);
    }

    const result: HttpResponse<T> = {
      data: (await parseBody(response, config.responseType)) as T,
      status: response.status,
      statusText: response.statusText,
      headers: collectHeaders(response),
      config,
    };

    if (!response.ok) {
      throw new HttpError(
        `Request failed with status code ${response.status}`,
        config,
        result as HttpResponse<any>,
        "ERR_BAD_RESPONSE",
      );
    }

    return result;
  }

  async function request<T>(
    userConfig: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    let config: HttpRequestConfig = {
      ...defaults,
      ...userConfig,
      headers: { ...(defaults.headers ?? {}), ...(userConfig.headers ?? {}) },
    };

    for (const handler of interceptors.request.handlers) {
      try {
        if (handler.fulfilled) config = await handler.fulfilled(config);
      } catch (error) {
        if (!handler.rejected) throw error;
        return handler.rejected(error) as Promise<HttpResponse<T>>;
      }
    }

    let promise: Promise<HttpResponse<any>> = send<any>(config);

    for (const handler of interceptors.response.handlers) {
      promise = promise.then(
        handler.fulfilled as Fulfilled<HttpResponse<any>> | undefined,
        handler.rejected,
      ) as Promise<HttpResponse<any>>;
    }

    return promise as unknown as Promise<HttpResponse<T>>;
  }

  const client = ((config: HttpRequestConfig) => request(config)) as HttpClient;

  client.request = request;
  client.defaults = defaults;
  client.interceptors = interceptors;

  const withoutBody = ["get", "delete", "head"] as const;
  withoutBody.forEach((method) => {
    client[method] = ((url: string, config: HttpRequestConfig = {}) =>
      request({
        ...config,
        url,
        method: method.toUpperCase() as HttpMethod,
      })) as never;
  });

  const withBody = ["post", "put", "patch"] as const;
  withBody.forEach((method) => {
    client[method] = ((
      url: string,
      data?: unknown,
      config: HttpRequestConfig = {},
    ) =>
      request({
        ...config,
        url,
        data: data === undefined ? config.data : data,
        method: method.toUpperCase() as HttpMethod,
      })) as never;
  });

  return client;
}

/** For absolute-URL calls that want no baseURL and no shared interceptors. */
export const http = createHttpClient();
